<template>
  <div class="grid">
    <div class="header f-h f-ac m-tx4">
      <cdn-svg path="nucleo/donut-group" class="h-x5 w-x5 m-rx1" />
      <div class="title t-lg bold">Donut Channels</div>
    </div>

    <div v-if="isInitialLoading" class="f-jc m-tx4">
      <donut-loading-dots />
    </div>
    <div v-else class="pos-rel">
      <donut-table
        class="m-tx3"
        :headers="tableHeaders"
        :data="tableData"
        :scrolls="false"
      >
        <template #channel="{ entry }">
          <div class="t single-line" :title="entry.channel">
            <a class="link ul" :href="entry.channel_url" target="_blank">{{ entry.channel }}</a>
          </div>
        </template>

        <template #type="{ entry }">
          <div class="t single-line">
            <span v-if="entry.type === 'intros'">☕ Intros</span>
            <span v-else-if="entry.type === 'watercooler'">🚰 Watercooler</span>
            <span v-else-if="entry.type === 'celebrations'">🎂 Celebrations</span>
          </div>
        </template>

        <template #members="{ entry }">
          <div class="t f-jc">{{ entry.members?.toLocaleString?.() || entry.members }}</div>
        </template>

        <template #owner="{ entry }">
          <div class="t single-line">{{ entry.owner }}</div>
        </template>

        <template #status="{ entry }">
          <div class="t single-line">
            <span v-if="entry.status === 'active'">✅ Active</span>
            <span v-else-if="entry.status === 'ghosted'">👻 Ghosted</span>
            <span v-else-if="entry.status === 'excluded'">🚫 Excluded</span>
            <div v-else-if="entry.status === 'snoozing'">
              😴 Snoozing
              <div v-if="entry.snoozing_until" class="t-sm gray">Until {{ entry.snoozing_until }}</div>
            </div>
            <span v-else>🚫 Not a member</span>
          </div>
        </template>

        <template #actions="{ entry }">
          <div class="f-ac f-jc">
            <template v-if="entry.type === 'intros' && entry.is_member">
              <template v-if="entry.status === 'active'">
                <donut-selector-single-static
                  :options="entry.snooze_options || []"
                  class="flexed"
                  placeholder="Snooze Until..."
                  @update:modelValue="onSnoozeSelected(entry, $event)"
                />
              </template>
              <template v-else>
                <donut-button class="inv" @click="onSetStatus(entry, 'active')">
                  {{ entry.status === 'snoozing' ? 'Stop Snoozing' : 'Resume Intros' }}
                </donut-button>
              </template>
            </template>
          </div>
        </template>
      </donut-table>
      <div v-if="isOverlayLoading" class="table-loading-overlay f-jc f-ac">
        <donut-loading-dots />
      </div>
    </div>

    <donut-pagination
      v-if="numPages > 1"
      class="m-tx2"
      :num-pages="numPages"
      :current-page="currentPage"
      page-change-action="settingsDonutChannels/changePage"
    />

    <div v-if="!loading && !tableData.length" class="f-jc m-vx2">
      <div class="t">No Donut channels found.</div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';

const { state, getters, dispatch } = useStore();

const tableHeaders = [
  { label: 'Channel', key: 'channel', weight: 10 },
  { label: 'Channel Type', key: 'type', weight: 5 },
  { label: 'Members', key: 'members', centered: true, weight: 3 },
  { label: 'Owner', key: 'owner', weight: 8 },
  { label: 'Status', key: 'status', weight: 6 },
  { label: '', key: 'actions', weight: 8 },
];

const tableData = computed(() => getters['settingsDonutChannels/rows'] || []);
const currentPage = computed(() => state.settingsDonutChannels?.currentPage || 0);
const numPages = computed(() => getters['settingsDonutChannels/numPages'] || 1);
const loading = computed(() => getters['settingsDonutChannels/loading']);
const isInitialLoading = computed(() => loading.value && currentPage.value === 0 && (!tableData.value || tableData.value.length === 0));
const isOverlayLoading = computed(() => loading.value && !isInitialLoading.value);

async function onSnoozeSelected(entry, selectedOption) {
  if (!selectedOption) return;
  const value = selectedOption.value || selectedOption;
  await dispatch('settingsDonutChannels/updateProgramStatus', {
    programId: entry.program_id,
    status: 'snoozed',
    daysToSnooze: parseInt(value, 10),
  });
  dispatch('snoozeIntros/fetchSnoozeStatus', state.currentUser.id);
}

async function onSetStatus(entry, status) {
  await dispatch('settingsDonutChannels/updateProgramStatus', {
    programId: entry.program_id,
    status,
  });
  dispatch('snoozeIntros/fetchSnoozeStatus', state.currentUser.id);
}

onMounted(() => {
  dispatch('settingsDonutChannels/fetchPage');
});

// React when the tab is clicked/selected
watch(() => state.selectedTab, (val, _old) => {
  if (val === 'donut-channels') {
    dispatch('settingsDonutChannels/fetchPage');
  }
});
</script>

<style scoped lang="scss">
  .table-loading-overlay {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(255,255,255,0.6);
  }
</style>
